import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Play, ArrowRight } from 'lucide-react';
import { useT } from '@/lib/i18n';
import HeroPipelinePremium from './premium/HeroPipelinePremium';

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const Hero = ({ user }) => {
  const { t, lang } = useT();
  const headline = lang === 'fr'
    ? <>Transformez <span className="text-[#D4FF00]">un podcast</span> en 30 jours de contenu viral.</>
    : <>Turn <span className="text-[#D4FF00]">one podcast</span> into 30 days of viral content.</>;
  const subtitle = lang === 'fr'
    ? "Uploadez un podcast, une interview ou un live. HighlightAI trouve automatiquement les meilleurs moments, recadre en 9:16, ajoute les sous-titres animés et prépare tout pour TikTok, Reels et Shorts."
    : "Upload one podcast, interview, or livestream. HighlightAI automatically finds the best moments, creates vertical clips, adds animated subtitles, and prepares everything for TikTok, Instagram Reels, and YouTube Shorts.";

  return (
    <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 dot-grid" data-testid="landing-hero">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: copy */}
        <div>
          <motion.div
            initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs uppercase tracking-[0.2em] text-zinc-400 mb-6"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00] animate-pulse" />
            {lang === 'fr' ? 'Moteur IA · Whisper + GPT-5.2' : 'AI Engine · Whisper + GPT-5.2'}
          </motion.div>

          <motion.h1
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.1, duration: 0.6 }}
            className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6"
            data-testid="hero-title"
          >
            {headline}
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.2, duration: 0.6 }}
            className="text-lg sm:text-xl text-zinc-400 leading-relaxed max-w-xl mb-10"
            data-testid="hero-subtitle"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.3, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <Link to={user ? '/upload' : '/register'}>
              <Button
                className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-12 px-7 text-base w-full sm:w-auto glow-accent"
                data-testid="hero-cta-primary"
              >
                {lang === 'fr' ? 'Démarrer gratuitement' : 'Start free'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button
                variant="outline"
                className="border-white/15 bg-transparent text-white hover:bg-white/5 h-12 px-7 text-base w-full sm:w-auto"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-4 h-4 mr-2" />
                {lang === 'fr' ? 'Voir la démo' : 'Watch demo'}
              </Button>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-10 flex items-center gap-4 text-xs text-zinc-500"
          >
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`w-7 h-7 rounded-full border-2 border-black bg-gradient-to-br ${
                  ['from-orange-400 to-pink-500', 'from-blue-400 to-purple-500', 'from-emerald-400 to-cyan-500', 'from-yellow-400 to-orange-500'][i - 1]
                }`} />
              ))}
            </div>
            <span>{lang === 'fr' ? '12 400+ créateurs · Aucune carte requise' : '12,400+ creators · No credit card required'}</span>
          </motion.div>
        </div>

        {/* Right: animated pipeline (10-15s scripted loop of real product) */}
        <div className="hidden lg:block">
          <HeroPipelinePremium />
        </div>
      </div>
    </section>
  );
};

export default Hero;
